// @flow
import { takeEvery, call, put, spawn, select } from 'redux-saga/effects';
import type { Saga } from 'redux-saga';
import {
  PRODUCTS,
  receiveCartProducts,
  setFavorites,
} from 'services/actions/productsActions';
import { setCartData, failedCartRequest } from 'services/actions/cartActions';
import {
  receiveSearchTerms,
  receiveSearchTermsError,
} from 'services/actions/searchActions';
import { receiveCategories } from 'services/actions/categoriesActions';
import { getCategories, getSearchTerms } from 'services/api/sale';
import { getCartData } from 'services/api/carts';
import { getFavoritesData, getProductsById } from 'services/api/products';
import { fetchSocialEcommData } from './social-ecomm';
import type { CartResponse, Favorites } from '../../sharedTypes';

const { REQUEST_FAVORITES } = PRODUCTS;

const formatSubscribableProducts = (
  products,
  variants,
  variantCadenceTypes
) => {
  const subscribableProductIds = [];
  const nonSubscribableProducts = {};
  const nonSubscribableVariants = {};
  Object.keys(products).forEach((productId) => {
    products[productId].variantIds.map((variantId) => {
      if (
        // if the variant is subscribable and the product is not yet in the subscribable list
        variantCadenceTypes[variantId] &&
        !subscribableProductIds.includes(productId)
      ) {
        // add the product id to the list of subscribable product ids
        subscribableProductIds.push(productId);
      } else {
        // otherwise add the product/variant data to nonsubscribable data
        nonSubscribableProducts[productId] = products[productId];
        nonSubscribableVariants[variantId] = variants[variantId];
      }
    });
  });
  return {
    subscribableProductIds,
    nonSubscribableProducts,
    nonSubscribableVariants,
  };
};

// watcher sagas
export function* watchFetchSaleData(): Saga<void> {
  yield takeEvery(REQUEST_FAVORITES, fetchFavoritesData);
}

// worker sagas
// These are calls made when the sale page loads
export function* fetchSaleData(flags) {
  const { socialWindow } = yield select((state) => state.campaignState);
  const { loggedIn } = yield select((state) => state.userState);
  /* using spawn so that these workers stay independent of each other even if they fail.
    https://redux-saga.js.org/docs/advanced/ForkModel.html#detached-forks-using-spawn */
  if (loggedIn) {
    yield spawn(fetchFavoritesData);
    yield spawn(fetchCartData); // we need cart data for product cards
  }
  yield spawn(fetchCategories, loggedIn);
  yield spawn(fetchSearchTerms, loggedIn);
  if (socialWindow) yield spawn(fetchSocialEcommData, flags);
}

function* fetchCategories(loggedIn) {
  const categoriesResponse = yield call(getCategories, loggedIn);
  yield put(receiveCategories(categoriesResponse.data.items[0].categories));
}

export function* fetchFavoritesData() {
  const favortiesData: Favorites = yield call(getFavoritesData);
  yield put(setFavorites(favortiesData));
}

export function* fetchCartData(): Saga<void> {
  /* using try catch here so that if this worker SAGA fails, 
  it prevents the user from seeing the cart. */
  try {
    const cartData: CartResponse = yield call(getCartData);
    const { products, variants, cartDetails } = cartData;
    const {
      subscribableProductIds,
      nonSubscribableProducts,
      nonSubscribableVariants,
    } = formatSubscribableProducts(
      products,
      variants,
      cartDetails.variantCadenceTypes
    );
    // we want to avoid overwriting product data with cart data for subscribable products
    // this is because fields such as price and inventory are different for those products
    // instead we should grab subscribable product data by using the ids
    // we need to do this so that subscribable products that have not yet loaded in the sale still behave as expected
    if (subscribableProductIds.length) {
      const subscribableProductData = yield call(
        getProductsById,
        subscribableProductIds,
        true
      );
      const { products: subscribableProducts, variants: subscribableVariants } =
        subscribableProductData;
      yield put(
        receiveCartProducts({
          products: { ...nonSubscribableProducts, ...subscribableProducts },
          variants: { ...nonSubscribableVariants, ...subscribableVariants },
        })
      );
    } else {
      yield put(
        receiveCartProducts({
          products: nonSubscribableProducts,
          variants: nonSubscribableVariants,
        })
      );
    }

    yield put(setCartData(cartDetails));
  } catch {
    yield put(failedCartRequest());
  }
}

function* fetchSearchTerms(loggedIn) {
  try {
    const response = yield call(getSearchTerms, loggedIn);
    const {
      data: { items },
    } = response;
    yield put(receiveSearchTerms(items));
  } catch (err) {
    yield put(receiveSearchTermsError(err));
  }
}
